import { useState, useEffect } from "react";
import { useLogin, useNotify } from "react-admin";
import { Login } from "react-admin";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  CardHeader,
  Button,
  Link,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { colors, radius, shadows } from "../../data/tokens";
import { PUBLIC_REGISTRATION_ENABLED } from "../../data/features";
import { determineUserReadableRole } from "../../ra-components/users/UserUtils";

const LoginPage = () => {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remindMe, setRemindMe] = useState(false);
  // const redirect = useRedirect();
  const login = useLogin();
  const notify = useNotify();

  // const handleClick = (location: string) => {
  //   redirect(location);
  // };

  // A normalização do identificador acontece no envio (handleSubmit): o
  // servidor entende e-mail, CPF e CNPJ com ou sem pontuação. A máscara
  // que existia aqui formatava como CPF QUALQUER número de 11+ dígitos
  // (regex sem âncoras) — um CNPJ digitado cru virava "333.523.940-0104"
  // e o login nunca encontrava o sócio coletivo.

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Espaço colado junto do e-mail/documento é erro comum e fazia o
    // login falhar com "Usuário não encontrado" (a busca no servidor é
    // por igualdade exata). A senha fica como digitada — pode conter
    // espaço legítimo.
    const cleanIdentifier = identifier.trim();
    // "Lembrar-me" guarda SÓ o identificador. A senha nunca é
    // persistida: a versão herdada gravava a senha em texto puro no
    // localStorage, legível por qualquer pessoa com acesso à máquina —
    // guardar senha é papel do gerenciador do navegador (criptografado).
    // Desmarcar o Lembrar-me também esquece o identificador gravado.
    if (remindMe) {
      localStorage.setItem("username", cleanIdentifier);
    } else {
      localStorage.removeItem("username");
    }

    // manual login fetch to test error response
    // console.log("pretest", process.env.REACT_APP_API_PATH);

    await fetch(`${process.env.REACT_APP_API_PATH}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ identifier: cleanIdentifier, password }),
    })
      .then(async (response) => {
        // Rate limit do servidor (5 tentativas/min) — mensagem amigável
        // pelo status, não pelo texto cru "ThrottlerException" do NestJS
        if (response.status === 429) {
          notify(
            "Muitas tentativas de acesso. Aguarde um minuto e tente novamente.",
            { type: "warning" }
          );
          return;
        }
        const responseJson = await response.json();
        if (responseJson.message) {
          notify(responseJson.message);
        } else {
          const userProfile = await fetch(
            `${process.env.REACT_APP_API_PATH}/auth/profile`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization:
                  responseJson && responseJson.access_token
                    ? "Bearer " + responseJson.access_token
                    : "",
              },
            }
          );
          if (!userProfile.ok) throw new Error(userProfile.statusText);
          const theUser = await userProfile.json();
          const userPermissions = theUser.role.permissions;
          // Destino pós-login por papel: admin e finance abrem o
          // Dashboard; secretaria e comunicação NÃO têm essa rota
          // (App.js só a registra para os dois primeiros) e caem na
          // lista de Sócios; o sócio vai para a própria ficha.
          const readableRole = determineUserReadableRole(userPermissions);
          const redirectByRole: any = {
            admin: "/dashboard",
            finance: "/dashboard",
            secretary: "/users/crud",
            comms: "/users/crud",
            user: "/users/crud/" + theUser.id + "/show",
          };
          login(
            {
              identifier: cleanIdentifier,
              password: password,
            },
            redirectByRole[readableRole] || "/users/crud"
          );
        }
      })
      .catch((error) => {
        notify(error.message);
      });

    // login({
    //   identifier: identifier,
    //   password: password,
    // }).catch(() => {
    //   notify(
    //     localStorage.getItem("loginError") || "E-mail ou CPF ou senha inválidos"
    //   );
    // });
  };
  useEffect(() => {
    // Faxina da chave herdada: versões antigas gravavam a SENHA em
    // texto puro no navegador de quem marcava "Lembrar-me" — apaga o
    // resíduo das máquinas que ainda o carregam. A senha em si fica a
    // cargo do gerenciador do navegador.
    localStorage.removeItem("password");
    const username = localStorage.getItem("username");
    if (username) {
      setIdentifier(username);
      setRemindMe(true);
    }
  }, []);

  return (
    <Login
      sx={{
        padding: 3,
        justifyContent: "center",
        // Fundo chapado no navy da marca — o gradiente radial que
        // aparecia aqui era o padrão da biblioteca, com tons fora da
        // paleta do Clube
        backgroundImage: "none",
        backgroundColor: colors.primaryDark,
        "& .RaLogin-card": {
          marginTop: 0,
          width: "100%",
          maxWidth: 430,
          borderRadius: `${radius.lg}px`,
          boxShadow: shadows.card,
        },
        "& .RaLogin-avatar": {
          display: "none",
        },
      }}
    >
      {/* Conteúdo direto no cartão do próprio <Login> — antes havia
          Paper + Card aninhados DENTRO dele (cartão dentro de cartão),
          e as camadas comiam o respiro lateral dos campos */}
      <Box sx={{ textAlign: "center", pt: 4, px: 4 }}>
        <img
          src="/logo-horizontal.svg"
          alt="Clube de Engenharia"
          style={{ maxWidth: 280, width: "100%" }}
        />
      </Box>
      <CardHeader
        title="Acesso Clube de Engenharia"
        subheader="Faça seu Login"
        sx={{ textAlign: "center", pt: 1.5, pb: 0 }}
      />
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "20px 32px 28px",
          }}
        >
          {/* name + autoComplete: é o que o gerenciador de senhas do
              navegador usa para reconhecer o formulário de login com
              confiança — sem eles, o "salvar senha?" e o preenchimento
              automático dependem de heurística e falham às vezes */}
          <TextField
            id="identifier"
            name="username"
            autoComplete="username"
            label="E-mail ou CPF"
            placeholder="Informe seu e-mail ou CPF"
            required
            type="text"
            value={identifier}
            fullWidth
            onChange={(e) => setIdentifier(e.target.value)}
          />
          <TextField
            id="password"
            name="password"
            autoComplete="current-password"
            placeholder="Informe sua senha"
            label="Senha"
            required
            fullWidth
            value={password}
            type={showPassword ? "text" : "password"}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Ocultar senha" : "Mostrar senha"
                    }
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Margem negativa encosta o "Lembrar-me" no campo de senha,
              agrupando os controles de acesso antes do botão */}
          <FormGroup sx={{ marginTop: -0.75 }}>
            <FormControlLabel
              label="Lembrar-me"
              control={
                <Checkbox
                  id="remember"
                  onChange={(e) => setRemindMe(!remindMe)}
                  checked={remindMe}
                />
              }
            />
          </FormGroup>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            fullWidth
            sx={{ height: 48 }}
          >
            Entrar
          </Button>
          <Box sx={{ textAlign: "center", marginTop: 1 }}>
            <Link
              href={window.location.origin + "/#/account-recovery"}
              underline="hover"
            >
              <b>Recuperar senha</b>
            </Link>
          </Box>
          {/* Atalho para o cadastro público — padrão de mercado: quem não
              tem conta resolve na própria tela de login, sem depender do
              menu lateral (que nem aparece nas telas de acesso). Some
              enquanto o cadastro estiver desativado (data/features.ts) */}
          {PUBLIC_REGISTRATION_ENABLED && (
            <Box
              sx={{
                textAlign: "center",
                marginTop: 0.5,
                fontSize: 14,
                color: colors.textSecondary,
              }}
            >
              Não tem conta?{" "}
              <Link
                href={window.location.origin + "/#/register"}
                underline="hover"
              >
                <b>Cadastre-se</b>
              </Link>
            </Box>
          )}
        </Box>
      </form>
    </Login>
  );
};

export default LoginPage;
